/**
 * Drops third-party / benign-noise events before they reach Sentry, so the
 * dashboard only shows our own legitimate errors.
 *
 * What gets dropped (all confirmed foreign — zero overlap with our code):
 *  - In-app-browser injected bridge scripts (Instagram / SoFi webviews):
 *    frames like `dispatchToBridge`, `SCDynimacBridge`, `logLoginFieldDetected`.
 *  - Benign user actions: share-sheet cancel (`AbortError` / "cancellation of share").
 *  - Bare platform/network noise: Safari "Load failed", extension `response.innerHTML`.
 *
 * Safety invariant: our own errors are ALWAYS `ApiError` subclasses
 * (see `services/errors.ts`). The keep-guard short-circuits on those FIRST,
 * so a real `NetworkError`/`ServerError` is never dropped even if its text
 * happened to resemble a noise substring.
 */
import type { ErrorEvent } from '@sentry/react-native';

import { ApiError } from '@/services/errors';

/** Just the slice of Sentry's `EventHint` this filter reads. */
type NoiseHint = { originalException?: unknown };

/** Stack-frame function names that only exist in injected in-app-browser scripts. */
const DROP_FRAME_FUNCTIONS = new Set<string>([
  'dispatchToBridge',
  'SCDynimacBridge',
  'logLoginFieldDetected',
  'sendPostMessage',
  'mutationObserverCallback',
  // Instagram in-app browser (WKWebView) page-tracking injection.
  'setupIosCallbackHandler',
  'sendPageHideMessage',
  'sendDataToNative',
]);

/** Message fragments that only appear in foreign / benign noise. */
const DROP_MESSAGE_SUBSTRINGS = [
  'The object does not support the operation or argument.',
  'response.innerHTML',
  "Can't find variable: SCDynimacBridge",
  'Load failed',
  'Abort due to cancellation of share',
  // WKWebView host-bridge API our code never calls — only injected in-app-browser
  // scripts (e.g. Instagram) reference it.
  'window.webkit.messageHandlers',
];

export function isForeignNoise(event: ErrorEvent, hint?: NoiseHint): boolean {
  // Keep-guard FIRST: our own errors are always ApiError subclasses — never drop them.
  if (hint?.originalException instanceof ApiError) return false;

  const firstException = event.exception?.values?.[0];

  // Share-sheet cancellation and other AbortErrors are user actions, not bugs.
  if (firstException?.type === 'AbortError') return true;

  // Message-substring match across the raw exception, the event value, and the message.
  const candidates: string[] = [];
  const original = hint?.originalException;
  if (typeof original === 'string') {
    candidates.push(original);
  } else if (original && typeof original === 'object' && 'message' in original) {
    const message = (original as { message?: unknown }).message;
    if (typeof message === 'string') candidates.push(message);
  }
  if (firstException?.value) candidates.push(firstException.value);
  if (typeof event.message === 'string') candidates.push(event.message);

  if (candidates.some((text) => DROP_MESSAGE_SUBSTRINGS.some((frag) => text.includes(frag)))) {
    return true;
  }

  // Foreign stack-frame function names (injected webview bridges).
  const frames = event.exception?.values?.flatMap((value) => value.stacktrace?.frames ?? []) ?? [];
  return frames.some((frame) => frame.function != null && DROP_FRAME_FUNCTIONS.has(frame.function));
}
